import { useLocation, useNavigate } from "@tanstack/react-router";
import { FileBarChart, X } from "lucide-react";
import { useState } from "react";
import { AuditKanban } from "@/components/audits/AuditKanban";
import { AuditsList } from "@/components/audits/AuditsList";
import { StatusReportDialog } from "@/components/audits/StatusReportDialog";
import { PageHeader } from "@/components/shared/page-header";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSession } from "@/lib/auth-client";

const FILTER_LABELS: Record<string, string> = {
  active: "Active",
  complete: "Complete",
  blocked: "Blocked",
};

export function AuditsPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [reportOpen, setReportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 200);

  const role = session?.user?.role ?? "";
  const isAuditor = role === "compliance_auditor";
  const title = isAuditor ? "My Audits" : "Audit Pipeline";
  const description = isAuditor
    ? "Track your assigned compliance audits."
    : "Manage and review compliance audits across all projects.";

  const searchParams = new URLSearchParams(location.search);
  const statusFilter = searchParams.get("status") ?? undefined;
  const filterLabel = statusFilter ? FILTER_LABELS[statusFilter] : undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader title={title} description={description} />
        <div className="flex items-center gap-2 pt-1 shrink-0">
          {!isAuditor && (
            <Button variant="outline" size="sm" onClick={() => setReportOpen(true)}>
              <FileBarChart className="h-4 w-4 mr-1.5" />
              Status Report
            </Button>
          )}
          {filterLabel && (
            <Badge variant="secondary" className="gap-1.5 pl-3 pr-2 py-1 text-sm">
              {filterLabel}
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => navigate({ to: "/dashboard/audits" })}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      </div>
      <SearchInput
        value={searchQuery}
        onValueChange={setSearchQuery}
        placeholder="Search by project, account, template, or assignee…"
        className="max-w-sm"
      />
      {isAuditor ? (
        <AuditsList statusFilter={statusFilter} searchQuery={debouncedQuery} />
      ) : (
        <AuditKanban statusFilter={statusFilter} searchQuery={debouncedQuery} />
      )}
      <StatusReportDialog open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
}
